"use client";

import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  GalleryVertical,
  LayoutGrid,
  List,
  MapPin,
  Search,
  SlidersHorizontal,
  Tag,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  fetchMarketplaceServices,
  filterServices,
  filterServicesForCatalogGrid,
  sortServicesHighlightedFirst,
  type MarketplaceService,
} from "@/lib/marketplace-services";
import {
  buildPredictions,
  hasAnyPrediction,
  type PredictionGroups,
} from "@/lib/search-predictions";
import { useFavoriteServices } from "@/hooks/useFavoriteServices";
import { useServiceFilters } from "@/hooks/useServiceFilters";
import { useAuth } from "@/contexts/AuthContext";
import { titleMatchesSearchQuery } from "@/lib/slugify-service-title";
import { cn } from "@/lib/utils";
import { SCROLL_TO_SERVICES_FLAG_KEY, scrollToServicesSectionWhenReady } from "@/lib/mobile-bottom-nav";
import { ServiceCard } from "@/components/home/ServiceCard";
import { MarketplaceFavoritesSheet } from "@/components/home/MarketplaceFavoritesSheet";
import { MarketplaceMobileBottomNav } from "@/components/home/MarketplaceMobileBottomNav";

// ── Shared prediction dropdown ─────────────────────────────────────────────
function PredictionDropdown({
  groups,
  onSelect,
}: {
  groups: PredictionGroups;
  onSelect: (href: string, kind: "service" | "location" | "category", id: number) => void;
}) {
  const hasServices = groups.services.length > 0;
  const hasLocations = groups.locations.length > 0;
  const hasCategories = groups.categories.length > 0;
  if (!hasServices && !hasLocations && !hasCategories) return null;
  return (
    <div className="absolute left-0 right-0 top-[calc(100%+0.35rem)] z-50 overflow-hidden rounded-xl border border-border bg-card shadow-xl">
      {hasServices && (
        <div>
          <p className="px-3 pb-1 pt-2.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Usluge
          </p>
          <ul>
            {groups.services.map((item) => (
              <li key={`svc-${item.id}`}>
                <button
                  type="button"
                  className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-foreground transition-colors hover:bg-muted"
                  onMouseDown={(e) => { e.preventDefault(); onSelect(item.href, "service", item.id); }}
                >
                  <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate">{item.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {hasLocations && (
        <div className={hasServices ? "border-t border-border/50" : ""}>
          <p className="px-3 pb-1 pt-2.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Lokacije
          </p>
          <ul>
            {groups.locations.map((item) => (
              <li key={`loc-${item.id}`}>
                <button
                  type="button"
                  className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-foreground transition-colors hover:bg-muted"
                  onMouseDown={(e) => { e.preventDefault(); onSelect(item.href, "location", item.id); }}
                >
                  <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate">{item.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {hasCategories && (
        <div className={(hasServices || hasLocations) ? "border-t border-border/50" : ""}>
          <p className="px-3 pb-1 pt-2.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Kategorije
          </p>
          <ul className="pb-1">
            {groups.categories.map((item) => (
              <li key={`cat-${item.id}`}>
                <button
                  type="button"
                  className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-foreground transition-colors hover:bg-muted"
                  onMouseDown={(e) => { e.preventDefault(); onSelect(item.href, "category", item.id); }}
                >
                  <Tag className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate">{item.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const SEARCH_PREDICTIONS_DEBOUNCE_MS = 220;

/** Items per page for the marketplace grid. */
const SERVICES_PAGE_SIZE = 15;

type PageIndicator = number | "ellipsis-start" | "ellipsis-end";

/**
 * Sliding window of exactly 5 consecutive pages centred on `current`.
 * First page + last page are always anchored; ellipsis bridges any gap.
 *
 * Examples (total = 20):
 *   current=1  → 1 2 3 4 5 … 20
 *   current=5  → 1 … 3 4 5 6 7 … 20
 *   current=10 → 1 … 8 9 10 11 12 … 20
 *   current=18 → 1 … 16 17 18 19 20
 */
function visibleServicePages(current: number, total: number): PageIndicator[] {
  if (total <= 1) return [1];

  const WINDOW = 4;
  // Clamp window so it never goes below 1 or above total
  let winStart = Math.max(1, current - Math.floor(WINDOW / 2));
  let winEnd = winStart + WINDOW - 1;
  if (winEnd > total) {
    winEnd = total;
    winStart = Math.max(1, winEnd - WINDOW + 1);
  }

  const pages: PageIndicator[] = [];

  // Leading anchor
  if (winStart > 1) {
    pages.push(1);
    if (winStart > 2) pages.push("ellipsis-start");
  }

  // Sliding window
  for (let p = winStart; p <= winEnd; p++) pages.push(p);

  // Trailing anchor
  if (winEnd < total) {
    if (winEnd < total - 1) pages.push("ellipsis-end");
    pages.push(total);
  }

  return pages;
}

type MobileServicesLayout = "grid-2" | "grid-1" | "list";
const MOBILE_VIEW_STORAGE_KEY = "planner-services-mobile-view-mode";
const MOBILE_HOME_NAV_PREF_KEY = "planner-mobile-home-nav-enabled";
const PRIORITY_LOCATION_LABEL = "Širom BiH";

type FilterOptions = {
  categories: { id: number; name: string }[];
  locations: { id: number; label: string; city: string }[];
};

function ServicesFiltersPanel({
  idPrefix,
  filterOptions,
  loading,
  categoryIds,
  locationIds,
  highlightedOnly,
  toggleCategory,
  toggleLocation,
  toggleHighlighted,
  clearFilters,
  hasActiveFilters,
  showResetButton = true,
}: {
  idPrefix: string;
  filterOptions: FilterOptions;
  loading: boolean;
  categoryIds: number[];
  locationIds: number[];
  highlightedOnly: boolean;
  toggleCategory: (id: number) => void;
  toggleLocation: (id: number) => void;
  toggleHighlighted: () => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  showResetButton?: boolean;
}) {
  const p = idPrefix ? `${idPrefix}-` : "";
  return (
    <div className="space-y-4">
      <div className="flex items-center hidden lg:flex justify-between gap-2">
        <span className="flex items-center gap-2 text-sm font-semibold text-[16px]">
          Filteri
        </span>
        {showResetButton && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(
              "h-8 gap-1 text-xs transition-opacity",
              hasActiveFilters ? "opacity-100" : "pointer-events-none opacity-0"
            )}
            onClick={clearFilters}
            aria-hidden={!hasActiveFilters}
            tabIndex={hasActiveFilters ? 0 : -1}
          >
            <X className="h-3.5 w-3.5" />
            Resetuj
          </Button>
        )}
      </div>

      <div className="space-y-2">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Istaknuti oglasi
        </span>
        <Label
          htmlFor={`${p}highlighted`}
          className={cn(
            "flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-[13px] font-medium leading-tight transition-colors",
            highlightedOnly
              ? "bg-[#E8E8F3] hover:bg-[#E8E8F3] dark:bg-white/12 dark:hover:bg-white/12"
              : "bg-[#F9F9FD] hover:bg-[#E8E8F3] dark:bg-white/[0.04] dark:hover:bg-white/12"
          )}
        >
          <Checkbox
            id={`${p}highlighted`}
            checked={highlightedOnly}
            onCheckedChange={toggleHighlighted}
          />
          <span>Prikaži samo istaknute oglase</span>
        </Label>
      </div>

      <Accordion
        type="multiple"
        defaultValue={["kategorije", "lokacije"]}
        className="space-y-2"
      >
        <AccordionItem
          value="kategorije"
          className="!border-b border-[#F9F9FD] pb-4 dark:border-white/10"
        >
          <AccordionHeader>
            <AccordionTrigger className="rounded-md !px-0 py-0 hover:no-underline">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Kategorija
              </span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent className="grid grid-cols-2 gap-2 pt-2">
            {filterOptions.categories.length === 0 && !loading && (
              <p className="col-span-full text-xs text-muted-foreground">
                Nema kategorija
              </p>
            )}
            {filterOptions.categories.map((c) => (
              <Label
                key={c.id}
                htmlFor={`${p}cat-${c.id}`}
                className={cn(
                  "flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-[13px] font-medium leading-tight transition-colors",
                  categoryIds.includes(c.id)
                    ? "bg-[#E8E8F3] hover:bg-[#E8E8F3] dark:bg-white/12 dark:hover:bg-white/12"
                    : "bg-[#F9F9FD] hover:bg-[#E8E8F3] dark:bg-white/[0.04] dark:hover:bg-white/12"
                )}
              >
                <Checkbox
                  id={`${p}cat-${c.id}`}
                  checked={categoryIds.includes(c.id)}
                  onCheckedChange={() => toggleCategory(c.id)}
                />
                <span>{c.name}</span>
              </Label>
            ))}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem
          value="lokacije"
          className="border-none !border-t border-[#EAEAFD] dark:border-white/10"
        >
          <AccordionHeader>
            <AccordionTrigger className="rounded-md !px-0 py-0 hover:no-underline">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Lokacija
              </span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent className="grid grid-cols-2 gap-2 pt-2">
            {filterOptions.locations.length === 0 && !loading && (
              <p className="col-span-full text-xs text-muted-foreground">
                Nema lokacija
              </p>
            )}
            {filterOptions.locations.map((l) => (
              <Label
                key={l.id}
                htmlFor={`${p}loc-${l.id}`}
                className={cn(
                  "flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-[13px] font-medium leading-tight transition-colors",
                  locationIds.includes(l.id)
                    ? "bg-[#E8E8F3] hover:bg-[#E8E8F3] dark:bg-white/12 dark:hover:bg-white/12"
                    : "bg-[#F9F9FD] hover:bg-[#E8E8F3] dark:bg-white/[0.04] dark:hover:bg-white/12"
                )}
              >
                <Checkbox
                  id={`${p}loc-${l.id}`}
                  checked={locationIds.includes(l.id)}
                  onCheckedChange={() => toggleLocation(l.id)}
                />
                <span>{l.label}</span>
              </Label>
            ))}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

/**
 * `initialServices` is the SSR-fetched marketplace catalog from
 * `app/home-client.tsx`. When present we seed React state with it so the list
 * renders in the initial HTML — no loading skeleton, no extra round-trip after
 * hydration. The mount-time fetch is skipped if a non-empty list is supplied
 * (the server-side cache already revalidates every 5 minutes via Next ISR).
 */
type ServicesSectionProps = {
  initialServices?: MarketplaceService[];
};

export function ServicesSection({ initialServices }: ServicesSectionProps = {}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { user } = useAuth();
  const {
    categoryIds,
    locationIds,
    highlightedOnly,
    toggleCategory,
    toggleLocation,
    toggleHighlighted,
    clearFilters,
  } = useServiceFilters();
  const { toggleFavorite, isFavorite, favoriteIds } = useFavoriteServices();

  const seededInitial = filterServicesForCatalogGrid(
    (initialServices ?? []) as MarketplaceService[],
  );
  const hasSeed = seededInitial.length > 0;
  const [allServices, setAllServices] = useState<MarketplaceService[]>(
    seededInitial,
  );
  const [loading, setLoading] = useState(!hasSeed);
  const [error, setError] = useState<string | null>(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [favoritesSheetOpen, setFavoritesSheetOpen] = useState(false);
  const [mobileSearchDraft, setMobileSearchDraft] = useState("");
  const [isDesktopSearchFocused, setIsDesktopSearchFocused] = useState(false);
  const [isMobileSearchFocused, setIsMobileSearchFocused] = useState(false);
  const [debouncedDesktopPrediction, setDebouncedDesktopPrediction] = useState(
    ""
  );
  const [debouncedMobilePrediction, setDebouncedMobilePrediction] = useState("");
  const [mobileNavigationEnabled, setMobileNavigationEnabled] = useState(
    () => {
      if (typeof window === "undefined") return true;
      return window.localStorage.getItem(MOBILE_HOME_NAV_PREF_KEY) !== "0";
    }
  );
  const [favoritesUiReady, setFavoritesUiReady] = useState(false);
  useEffect(() => {
    setFavoritesUiReady(true);
  }, []);
  const favCount = favoritesUiReady ? favoriteIds.size : 0;
  const [sectionInView, setSectionInView] = useState(false);
  const mobileSearchSubmittedRef = useRef(false);
  const sectionRef = useRef<HTMLElement | null>(null);
  const filterKeyRef = useRef(
    `${categoryIds.join(",")}|${locationIds.join(",")}|${highlightedOnly ? "1" : "0"}`
  );
  const [servicesPage, setServicesPage] = useState(
    () => Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1)
  );
  const [titleSearch, setTitleSearch] = useState(
    () => searchParams.get("q") ?? ""
  );
  const [debouncedTitleSearch, setDebouncedTitleSearch] = useState(
    () => searchParams.get("q") ?? ""
  );
  /** Below `lg`: 2-col grid, 1-col grid, or list rows; from `lg` layout is always 3-col grid. */
  const [mobileViewMode, setMobileViewMode] = useState<MobileServicesLayout>(
    () => {
      if (typeof window === "undefined") return "list";
      const raw = window.localStorage.getItem(MOBILE_VIEW_STORAGE_KEY);
      return raw === "grid-1" || raw === "grid-2" || raw === "list"
        ? raw
        : "list";
    }
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(MOBILE_VIEW_STORAGE_KEY, mobileViewMode);
  }, [mobileViewMode]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const readPreference = () =>
      window.localStorage.getItem(MOBILE_HOME_NAV_PREF_KEY) !== "0";

    const handlePreferenceEvent = (
      event: Event | CustomEvent<{ enabled?: boolean }>
    ) => {
      const detail = (event as CustomEvent<{ enabled?: boolean }>).detail;
      if (detail && typeof detail.enabled === "boolean") {
        setMobileNavigationEnabled(detail.enabled);
        return;
      }
      setMobileNavigationEnabled(readPreference());
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== MOBILE_HOME_NAV_PREF_KEY) return;
      setMobileNavigationEnabled(readPreference());
    };

    window.addEventListener(
      "planner:mobile-nav-preference",
      handlePreferenceEvent as EventListener
    );
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(
        "planner:mobile-nav-preference",
        handlePreferenceEvent as EventListener
      );
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  useEffect(() => {
    if (mobileNavigationEnabled) return;
    setMobileSearchOpen(false);
  }, [mobileNavigationEnabled]);

  /*
   * Skip the mount-time client fetch when SSR already streamed the catalog
   * (the normal path on `/`). Other entry points that mount this section
   * without a seeded list still fall back to a client fetch.
   */
  useEffect(() => {
    if (hasSeed) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchMarketplaceServices()
      .then((data) => {
        if (!cancelled) setAllServices(filterServicesForCatalogGrid(data));
      })
      .catch(() => {
        if (!cancelled) setError("Nije moguće učitati usluge.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hasSeed]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedDesktopPrediction(titleSearch);
    }, SEARCH_PREDICTIONS_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [titleSearch]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedMobilePrediction(mobileSearchDraft);
    }, SEARCH_PREDICTIONS_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [mobileSearchDraft]);

  const commitSearchToUrl = useCallback(
    (value: string) => {
      const p = new URLSearchParams(searchParams.toString());
      if (value) p.set("q", value);
      else p.delete("q");
      const qs = p.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    },
    [searchParams, router, pathname]
  );

  /** Apply search immediately (button click or Enter); still updates `q` via debounced-sync effect. */
  const goToServicesTop = useCallback(() => {
    scrollToServicesSectionWhenReady();
  }, []);

  const applySearchNow = useCallback((scrollToTop?: boolean) => {
    setDebouncedTitleSearch(titleSearch);
    if (scrollToTop) goToServicesTop();
  }, [titleSearch, goToServicesTop]);

  const handleMobileSearchOpenChange = useCallback((open: boolean) => {
    if (!open && !mobileSearchSubmittedRef.current) {
      setMobileSearchDraft("");
    }
    if (open) {
      mobileSearchSubmittedRef.current = false;
    }
    setMobileSearchOpen(open);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.sessionStorage.getItem(SCROLL_TO_SERVICES_FLAG_KEY) !== "1") {
      return;
    }
    window.sessionStorage.removeItem(SCROLL_TO_SERVICES_FLAG_KEY);
    scrollToServicesSectionWhenReady();
  }, []);

  /** Sync `q` when debounced value changes (avoids router.replace on every keystroke). */
  useEffect(() => {
    const urlQ = searchParams.get("q") ?? "";
    if (debouncedTitleSearch === urlQ) return;
    commitSearchToUrl(debouncedTitleSearch);
  }, [debouncedTitleSearch, searchParams, commitSearchToUrl]);

  /**
   * When category/location filters change, preserve the already-committed search
   * term in the URL so `useServiceFilters` never drops `q`.
   */
  useEffect(() => {
    const key = `${categoryIds.join(",")}|${locationIds.join(",")}|${highlightedOnly ? "1" : "0"}`;
    if (key === filterKeyRef.current) return;
    filterKeyRef.current = key;
    commitSearchToUrl(debouncedTitleSearch);
  }, [categoryIds, locationIds, highlightedOnly, debouncedTitleSearch, commitSearchToUrl]);

  /** New filter/search → reset to page 1 and clear ?page from URL. */
  useEffect(() => {
    setServicesPage(1);
    const p = new URLSearchParams(window.location.search);
    if (p.has("page")) {
      p.delete("page");
      const qs = p.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    }
  }, [categoryIds, locationIds, highlightedOnly, debouncedTitleSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;

    const io = new IntersectionObserver(
      ([entry]) => {
        setSectionInView(entry.isIntersecting);
      },
      { threshold: 0.08, rootMargin: "-48px 0px 0px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const filterOptions = useMemo(() => {
    const catMap = new Map<number, { id: number; name: string }>();
    const locMap = new Map<
      number,
      { id: number; label: string; city: string }
    >();
    for (const s of allServices) {
      for (const c of s.categories) {
        if (!catMap.has(c.id)) catMap.set(c.id, { id: c.id, name: c.name });
      }
      for (const l of s.locations) {
        const locationNameLabel = (l.locationName ?? "").trim();
        const label = locationNameLabel;
        if (!label) continue;
        if (!locMap.has(l.id))
          locMap.set(l.id, {
            id: l.id,
            city: label,
            label,
          });
      }
    }
    return {
      categories: [...catMap.values()].sort((a, b) =>
        a.name.localeCompare(b.name, "sr")
      ),
      locations: [...locMap.values()].sort((a, b) => {
        const aPriority = a.label === PRIORITY_LOCATION_LABEL;
        const bPriority = b.label === PRIORITY_LOCATION_LABEL;
        if (aPriority && !bPriority) return -1;
        if (!aPriority && bPriority) return 1;
        return a.city.localeCompare(b.city, "sr");
      }),
    };
  }, [allServices]);

  const filtered = useMemo(
    () =>
      filterServices(allServices, {
        categoryIds,
        locationIds,
        highlightedOnly,
      }),
    [allServices, categoryIds, locationIds, highlightedOnly]
  );

  const filteredByTitle = useMemo(
    () =>
      filtered.filter((s) =>
        titleMatchesSearchQuery(s.title, debouncedTitleSearch)
      ),
    [filtered, debouncedTitleSearch]
  );

  const sortedForDisplay = useMemo(
    () => sortServicesHighlightedFirst(filteredByTitle),
    [filteredByTitle],
  );

  const totalServicesPages = Math.max(
    1,
    Math.ceil(sortedForDisplay.length / SERVICES_PAGE_SIZE)
  );

  const paginatedServices = useMemo(() => {
    const start = (servicesPage - 1) * SERVICES_PAGE_SIZE;
    return sortedForDisplay.slice(start, start + SERVICES_PAGE_SIZE);
  }, [sortedForDisplay, servicesPage]);

  useEffect(() => {
    setServicesPage((p) => Math.min(p, totalServicesPages));
  }, [totalServicesPages]);

  const goToServicesPage = useCallback(
    (next: number) => {
      const clamped = Math.min(Math.max(1, next), totalServicesPages);
      setServicesPage(clamped);
      // Persist page in URL so back/share works
      const p = new URLSearchParams(window.location.search);
      if (clamped === 1) p.delete("page");
      else p.set("page", String(clamped));
      const qs = p.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [totalServicesPages, router, pathname]
  );

  const hasActiveFilters =
    categoryIds.length > 0 || locationIds.length > 0 || highlightedOnly;
  const hasTitleSearch = titleSearch.trim().length > 0;
  const hasMobileSearchDraft = mobileSearchDraft.trim().length > 0;

  const clearTitleSearch = useCallback(() => {
    setTitleSearch("");
    setDebouncedTitleSearch("");
    commitSearchToUrl("");
  }, [commitSearchToUrl]);

  /** Single atomic URL replace that clears category, location AND q in one call. */
  const clearFiltersAndSearch = useCallback(() => {
    const p = new URLSearchParams(searchParams.toString());
    p.delete("category");
    p.delete("location");
    p.delete("highlighted");
    p.delete("q");
    const qs = p.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    setTitleSearch("");
    setDebouncedTitleSearch("");
    setMobileSearchDraft("");
  }, [searchParams, router, pathname]);

  /**
   * Atomically applies a location or category filter from the prediction
   * dropdown while clearing the search term (q + drafts) in one URL replace.
   */
  const applyFilterFromPrediction = useCallback(
    (kind: "location" | "category", id: number) => {
      const p = new URLSearchParams(searchParams.toString());
      p.delete("q");
      if (kind === "location") {
        const set = new Set(locationIds);
        set.add(id);
        p.set("location", [...set].sort((a, b) => a - b).join(","));
      } else {
        const set = new Set(categoryIds);
        set.add(id);
        p.set("category", [...set].sort((a, b) => a - b).join(","));
      }
      const qs = p.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
      setTitleSearch("");
      setDebouncedTitleSearch("");
      setMobileSearchDraft("");
    },
    [searchParams, router, pathname, locationIds, categoryIds]
  );

  const desktopPredictions = useMemo<PredictionGroups>(
    () => buildPredictions(debouncedDesktopPrediction, allServices),
    [allServices, debouncedDesktopPrediction]
  );

  const mobilePredictions = useMemo<PredictionGroups>(
    () => buildPredictions(debouncedMobilePrediction, allServices),
    [allServices, debouncedMobilePrediction]
  );

  const filterPanelProps = {
    filterOptions,
    loading,
    categoryIds,
    locationIds,
    highlightedOnly,
    toggleCategory,
    toggleLocation,
    toggleHighlighted,
    clearFilters: clearFiltersAndSearch,
    hasActiveFilters,
  };

  const servicesGridClassName = cn(
    "gap-3 lg:!grid lg:!grid-cols-3",
    mobileViewMode === "list" &&
      "flex flex-col lg:grid lg:grid-cols-3 lg:gap-3",
    mobileViewMode !== "list" &&
      cn(
        "grid",
        mobileViewMode === "grid-1" ? "grid-cols-1" : "grid-cols-2",
        "lg:grid-cols-3"
      )
  );

  const showMobileGridToggle =
    loading ||
    (filtered.length > 0 && filteredByTitle.length > 0);

  return (
    <section
      ref={sectionRef}
      id="usluge"
      className={cn(
        "scroll-mt-20 py-12 md:py-19",
        mobileNavigationEnabled ? "pb-12 md:pb-28 lg:pb-12" : "pb-12"
      )}
      aria-labelledby="usluge-heading"
    >
      <div className="mx-auto w-full max-w-[min(100%,96rem)] px-3">
        <div className="mb-8 grid items-center justify-center gap-4 md:mb-10 lg:grid-cols-[minmax(0,1fr)_minmax(320px,460px)] lg:gap-8">
          <h2
            id="usluge-heading"
            className="text-balance text-center leading-[120%] tracking-[0] text-foreground lg:text-left"
          >
            <span className="block font-alt text-[27px] font-medium  lg:text-[54px]">
              Istražite usluge.
            </span>
            <span className="block font-serif text-[#637384] leading-[1.6] text-[27px] font-medium italic lg:text-[54px]">
              Kreirajte svoj idealan izbor.
            </span>
          </h2>
          <p className="max-w-[46ch] text-center text-base font-normal leading-[150%] tracking-[0] text-[#314259] dark:text-[#94a3b8] lg:text-center">
            Izaberite kategoriju, odaberite lokaciju i u par klikova dođite do
            najboljih ponuda. Brzo, pregledno i jednostavno.
          </p>
        </div>

        <div className="flex flex-col gap-8 lg:flex-row lg:gap-4 xl:gap-4">
          {/* Sidebar filters — desktop; sticky na unutrašnjem panelu (radi uz overflow-x: clip na html/body) */}
          <aside className="hidden w-full shrink-0 lg:block lg:w-[300px]">
            <div className="sticky top-20 z-10 space-y-6 rounded-[15px] border border-border bg-card p-4 shadow-sm">
              <ServicesFiltersPanel idPrefix="desk" {...filterPanelProps} />
            </div>
          </aside>

          {/* Grid, then map below */}
          <div className="min-w-0 flex-1 space-y-8">
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}

            {!loading && !error && allServices.length > 0 && (
              <form
                className="relative mb-4 rounded-[15px] border border-border bg-card p-1.5 shadow-sm sm:p-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  applySearchNow();
                }}
              >
                <div className="flex items-center gap-2">
                  <div className="relative min-w-0 flex-1">
                    <Search
                      className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground sm:left-4 sm:h-5 sm:w-5"
                      strokeWidth={1.8}
                      aria-hidden
                    />
                    <Input
                      type="text"
                      name="usluge-pretraga"
                      role="searchbox"
                      inputMode="search"
                      enterKeyHint="search"
                      value={titleSearch}
                      onChange={(e) => {
                        setTitleSearch(e.target.value);
                      }}
                      onFocus={() => setIsDesktopSearchFocused(true)}
                      onBlur={() => {
                        window.setTimeout(
                          () => setIsDesktopSearchFocused(false),
                          200
                        );
                      }}
                      placeholder="Pretražite usluge..."
                      className={cn(
                        "h-10 w-full border-0 bg-transparent pl-10 text-[14px] leading-[1.5] shadow-none sm:h-12 sm:pl-12",
                        hasTitleSearch ? "pr-12" : "pr-3"
                      )}
                      aria-label="Pretraga usluga po nazivu"
                      autoComplete="off"
                    />
                    {hasTitleSearch && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        noScale
                        className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 rounded-[8px] text-muted-foreground hover:text-foreground sm:h-9 sm:w-9"
                        onClick={clearTitleSearch}
                        aria-label="Obriši pretragu"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <Button
                    type="submit"
                    variant="default"
                    size="sm"
                    className="h-10 shrink-0 rounded-[8px] px-4 text-sm font-semibold sm:h-12 sm:px-6"
                  >
                    Pretraži
                  </Button>
                </div>
                {isDesktopSearchFocused &&
                  hasAnyPrediction(desktopPredictions) && (
                    <PredictionDropdown
                      groups={desktopPredictions}
                      onSelect={(href, kind, id) => {
                        if (kind === "location") {
                          toggleLocation(id);
                          goToServicesTop();
                          setTitleSearch("");
                          setDebouncedTitleSearch("");
                        } else if (kind === "category") {
                          toggleCategory(id);
                          goToServicesTop();
                          setTitleSearch("");
                          setDebouncedTitleSearch("");
                        } else {
                          setIsDesktopSearchFocused(false);
                          router.push(href);
                        }
                      }}
                    />
                  )}
              </form>
            )}

            {showMobileGridToggle && (
              <div
                className="mb-3 flex justify-end gap-2 lg:hidden"
                role="group"
                aria-label="Način prikaza liste usluga"
              >
                <Button
                  type="button"
                  variant={mobileViewMode === "list" ? "default" : "outline"}
                  size="icon"
                  className="h-10 w-10 shrink-0 rounded-lg"
                  aria-pressed={mobileViewMode === "list"}
                  aria-label="Lista — red sa sličicom"
                  title="Lista"
                  onClick={() => setMobileViewMode("list")}
                >
                  <List className="h-5 w-5" aria-hidden />
                </Button>
                <Button
                  type="button"
                  variant={mobileViewMode === "grid-1" ? "default" : "outline"}
                  size="icon"
                  className="h-10 w-10 shrink-0 rounded-lg"
                  aria-pressed={mobileViewMode === "grid-1"}
                  aria-label="Mreža — jedna kolona"
                  title="Jedna kolona"
                  onClick={() => setMobileViewMode("grid-1")}
                >
                  <GalleryVertical className="h-5 w-5" aria-hidden />
                </Button>
                <Button
                  type="button"
                  variant={mobileViewMode === "grid-2" ? "default" : "outline"}
                  size="icon"
                  className="h-10 w-10 shrink-0 rounded-lg"
                  aria-pressed={mobileViewMode === "grid-2"}
                  aria-label="Mreža — dve kolone"
                  title="Dve kolone"
                  onClick={() => setMobileViewMode("grid-2")}
                >
                  <LayoutGrid className="h-5 w-5" aria-hidden />
                </Button>
              </div>
            )}

            {loading ? (
              <div className={cn(servicesGridClassName)}>
                {Array.from({ length: 8 }).map((_, i) =>
                  mobileViewMode === "list" ? (
                    <div
                      key={i}
                      className="flex gap-3 lg:h-72 lg:flex-col lg:gap-2"
                    >
                      <div className="h-[125px] w-[125px] min-w-[125px] shrink-0 animate-pulse rounded-xl bg-muted/60 lg:h-48 lg:w-full lg:min-w-0 lg:rounded-xl" />
                      <div className="flex min-w-0 flex-1 flex-col justify-center gap-2 py-1 lg:flex-initial lg:justify-between lg:pt-2">
                        <div className="h-4 w-full animate-pulse rounded bg-muted/60 lg:h-5" />
                        <div className="h-3 w-2/3 animate-pulse rounded bg-muted/60" />
                      </div>
                    </div>
                  ) : (
                    <div
                      key={i}
                      className="h-72 animate-pulse rounded-xl bg-muted/60"
                    />
                  )
                )}
              </div>
            ) : filtered.length === 0 ? (
              <div
                className={cn(
                  "rounded-xl border border-dashed border-border bg-card/50 px-6 py-12 text-center text-sm text-muted-foreground"
                )}
              >
                {allServices.length === 0
                  ? "Trenutno nema objavljenih usluga."
                  : "Nema usluga koje odgovaraju izabranim filterima."}
              </div>
            ) : filteredByTitle.length === 0 ? (
              <div
                className={cn(
                  "rounded-xl border border-dashed border-border bg-card/50 px-6 py-12 text-center text-sm text-muted-foreground"
                )}
              >
                Nema usluga koje odgovaraju pretrazi. Pokušajte drugačije slovo
                ili obrišite upit.
              </div>
            ) : (
              <>
                <div className={servicesGridClassName}>
                  {paginatedServices.map((s) => (
                    <ServiceCard
                      key={s.documentId ?? s.id}
                      service={s}
                      layout={mobileViewMode === "list" ? "list" : "grid"}
                      mobileViewMode={mobileViewMode}
                      isFavorite={isFavorite(s.id)}
                      onToggleFavorite={() => toggleFavorite(s)}
                    />
                  ))}
                </div>

                {totalServicesPages > 1 && (
                  <nav
                    className="flex flex-wrap items-center justify-center gap-1.5 pt-4"
                    aria-label="Stranice liste usluga"
                  >
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1 px-2.5 sm:px-3"
                      disabled={servicesPage <= 1}
                      onClick={() => goToServicesPage(servicesPage - 1)}
                      aria-label="Prethodna strana"
                    >
                      <ChevronLeft className="h-4 w-4 shrink-0" aria-hidden />
                      <span className="hidden sm:inline">Prethodna</span>
                    </Button>

                    {visibleServicePages(servicesPage, totalServicesPages).map(
                      (item, idx) =>
                        item === "ellipsis-start" || item === "ellipsis-end" ? (
                          <span
                            key={item}
                            className="flex h-9 w-9 items-center justify-center text-sm text-muted-foreground"
                            aria-hidden
                          >
                            …
                          </span>
                        ) : (
                          <Button
                            key={item}
                            type="button"
                            variant={
                              item === servicesPage ? "default" : "outline"
                            }
                            size="sm"
                            className="h-9 w-9 shrink-0 px-0 tabular-nums"
                            onClick={() => goToServicesPage(item)}
                            aria-label={`Strana ${item}`}
                            aria-current={
                              item === servicesPage ? "page" : undefined
                            }
                          >
                            {item}
                          </Button>
                        )
                    )}

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1 px-2.5 sm:px-3"
                      disabled={servicesPage >= totalServicesPages}
                      onClick={() => goToServicesPage(servicesPage + 1)}
                      aria-label="Sledeća strana"
                    >
                      <span className="hidden sm:inline">Sledeća</span>
                      <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
                    </Button>
                  </nav>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile: vertically centered, flush to left edge; fades in/out when section is in view */}
      <div
        className={cn(
          "fixed left-0 top-1/2 z-40 -translate-y-1/2 lg:hidden",
          "pl-[max(0px,env(safe-area-inset-left))]",
          "transition-all duration-300 ease-out",
          sectionInView
            ? "pointer-events-auto translate-x-0 opacity-100"
            : "pointer-events-none -translate-x-3 opacity-0"
        )}
        aria-hidden={!sectionInView}
      >
        <Button
          type="button"
          aria-label={
            hasActiveFilters
              ? `Filteri, ${
                  categoryIds.length +
                  locationIds.length +
                  (highlightedOnly ? 1 : 0)
                } aktivna`
              : "Filteri"
          }
          className="relative h-10 w-10 shrink-0 rounded-md rounded-l-none border-l-0 p-0 shadow-lg transition-transform duration-300 ease-out active:scale-[0.98]"
          onClick={() => setMobileFiltersOpen(true)}
        >
          <SlidersHorizontal className="mx-auto h-4.5 w-4.5" aria-hidden />
          {hasActiveFilters && (
            <span className="absolute -right-1 -top-1 flex h-4.5 min-w-4.5 items-center justify-center rounded-full border border-background bg-primary px-1 text-[10px] font-bold leading-none text-primary-foreground tabular-nums shadow-sm">
              {categoryIds.length +
                locationIds.length +
                (highlightedOnly ? 1 : 0) >
              9
                ? "9+"
                : categoryIds.length +
                  locationIds.length +
                  (highlightedOnly ? 1 : 0)}
            </span>
          )}
        </Button>
      </div>

      {mobileNavigationEnabled && (
        <MarketplaceMobileBottomNav
          favCount={favCount}
          homeActive
          profilHref={user ? "/profil" : "/prijava"}
          onGoToServices={goToServicesTop}
          onOpenFavorites={() => setFavoritesSheetOpen(true)}
          onOpenSearch={() => {
            setMobileSearchDraft(titleSearch);
            setMobileSearchOpen(true);
          }}
        />
      )}

      <Sheet
        open={mobileNavigationEnabled && mobileSearchOpen}
        onOpenChange={handleMobileSearchOpenChange}
      >
        <SheetContent
          side="top"
          className="rounded-b-2xl border-x-0 border-t-0 px-3 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))]"
        >
          <SheetHeader className="px-1 pb-2 text-left">
            <SheetTitle>Pretraga usluga</SheetTitle>
          </SheetHeader>
          <form
            className="relative rounded-2xl border border-border bg-card p-1.5 shadow-sm"
            onSubmit={(e) => {
              e.preventDefault();
              const next = mobileSearchDraft.trim();
              setTitleSearch(next);
              setDebouncedTitleSearch(next);
              commitSearchToUrl(next);
              mobileSearchSubmittedRef.current = true;
              setMobileSearchOpen(false);
              goToServicesTop();
            }}
          >
            <div className="flex items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.8}
                  aria-hidden
                />
                <Input
                  type="text"
                  name="mobilna-pretraga-usluga"
                  role="searchbox"
                  inputMode="search"
                  enterKeyHint="search"
                  value={mobileSearchDraft}
                  onChange={(e) => setMobileSearchDraft(e.target.value)}
                  onFocus={() => setIsMobileSearchFocused(true)}
                  onBlur={() => {
                    window.setTimeout(
                      () => setIsMobileSearchFocused(false),
                      200
                    );
                  }}
                  placeholder="Pretražite usluge..."
                  className={cn(
                    "h-10 w-full border-0 bg-transparent pl-10 text-[14px] leading-[1.5] shadow-none",
                    hasMobileSearchDraft ? "pr-10" : "pr-3"
                  )}
                  aria-label="Pretraga usluga po nazivu"
                  autoComplete="off"
                />
                {hasMobileSearchDraft && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    noScale
                    className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 rounded-[8px] text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setMobileSearchDraft("");
                      setTitleSearch("");
                      setDebouncedTitleSearch("");
                      commitSearchToUrl("");
                    }}
                    aria-label="Obriši pretragu"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <Button
                type="submit"
                variant="default"
                size="sm"
                className="h-10 shrink-0 rounded-[8px] px-4 text-sm font-semibold"
              >
                Pretraži
              </Button>
            </div>
            {isMobileSearchFocused && hasAnyPrediction(mobilePredictions) && (
              <PredictionDropdown
                groups={mobilePredictions}
                onSelect={(href, kind, id) => {
                  if (kind === "location") {
                    toggleLocation(id);
                    goToServicesTop();
                    setMobileSearchDraft("");
                    setMobileSearchOpen(false);
                  } else if (kind === "category") {
                    toggleCategory(id);
                    goToServicesTop();
                    setMobileSearchDraft("");
                    setMobileSearchOpen(false);
                  } else {
                    setIsMobileSearchFocused(false);
                    setMobileSearchOpen(false);
                    router.push(href);
                  }
                }}
              />
            )}
          </form>
        </SheetContent>
      </Sheet>

      <MarketplaceFavoritesSheet
        open={favoritesSheetOpen}
        onOpenChange={setFavoritesSheetOpen}
        catalogServices={allServices}
      />

      <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
        <SheetContent
          side="left"
          className="flex w-[min(100vw-2rem,20rem)] flex-col gap-0 p-0 sm:max-w-sm"
        >
          <SheetHeader className="border-b border-border px-6 pb-4 pt-6 text-left">
            <SheetTitle>Filteri</SheetTitle>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-24 pt-4">
            <ServicesFiltersPanel
              idPrefix="mob"
              {...filterPanelProps}
              showResetButton={false}
            />
          </div>
          <div className="border-t border-border bg-background/95 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] backdrop-blur">
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={clearFiltersAndSearch}
                disabled={!hasActiveFilters && !hasTitleSearch}
              >
                Reset filtere
              </Button>
              <Button
                type="button"
                className="w-full"
                onClick={() => {
                  setMobileFiltersOpen(false);
                  requestAnimationFrame(() => {
                    goToServicesTop();
                  });
                }}
              >
                Rezultati ({filteredByTitle.length})
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </section>
  );
}
